import { Component, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges, TemplateRef } from '@angular/core';
import { CriteriaOperator } from '@shared/models/types/types.generated';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import { SelectionOption } from '@shared/models/types/selection-option';
import { pairwise, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { generateUUID, isSubfilterOperator, isUnaryOperator } from '@shared/utils/utils';
import { valuePreservingTransitions } from './filter-criteria-operator-transitions';

export interface FilterCriteriaFormModel<T> {
  key?: FormControl<T>;
  operator?: FormControl<CriteriaOperator>;
  value?: FormControl<string>;
}

@Component({
  standalone: false,
  selector: 'app-filter-criteria',
  templateUrl: './filter-criteria.component.html',
  styleUrls: ['./filter-criteria.component.scss'],
})
export class FilterCriteriaComponent<T> implements OnInit, OnChanges {
  i18Next: I18NextPipe = inject(I18NextPipe);

  @Input() formGroup: FormGroup<FilterCriteriaFormModel<T>>;
  @Input() filterKeyOptions: T[];
  @Input() valueTemplate: TemplateRef<any>;
  @Input() settingsTemplate?: TemplateRef<any>;
  @Input() allowedOperators?: CriteriaOperator[] = Object.values(CriteriaOperator);
  @Input() showOperators: boolean = true;
  @Input() displayValue?: string;

  collapsed: boolean = false;
  uuid: string = generateUUID().toString();

  translatedFilterKeyOptions: SelectionOption<T>[];
  private destroyRef$ = inject(DestroyRef);

  ngOnInit(): void {
    this.translatedFilterKeyOptions = this.filterKeyOptions
      .map((option: T) => ({
        label: this.i18Next.transform(('common:filterKeys.' + option) as string),
        value: option,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  ngOnChanges(changes: SimpleChanges) {
    const formGroupChange = changes.formGroup?.currentValue as FormGroup<FilterCriteriaFormModel<T>>;

    if (formGroupChange) {
      this.watchFormChanges();
    }
  }

  toggleCollapsed() {
    this.collapsed = !this.collapsed;
  }

  get label() {
    const key = this.i18Next.transform(('common:filterKeys.' + this.formGroup.value.key) as string);
    const operator = this.formGroup.value.operator;
    const operatorStr = this.i18Next.transform('common:filterOperators.' + operator);
    const value = this.displayValue ? this.displayValue : this.formGroup.value.value;

    if (isUnaryOperator(operator)) {
      return key && operatorStr ? `${key} ${operatorStr}` : key;
    } else if (isSubfilterOperator(operator)) {
      return key && operatorStr ? `${key} ${operatorStr} (subfilter)` : key;
    } else {
      return key && operatorStr && value ? `${key} ${operatorStr} ${value}` : key;
    }
  }

  static emptyFilterCriteriaGroup<T>(valueRequired: boolean) {
    return new FormGroup<FilterCriteriaFormModel<T>>({
      key: new FormControl<T>(undefined, Validators.required),
      operator: new FormControl<CriteriaOperator>(CriteriaOperator.Equals, Validators.required),
      value: new FormControl<string>(null, valueRequired ? Validators.required : null),
    });
  }

  private watchFormChanges() {
    const valueCtrl = this.formGroup.controls.value;

    this.formGroup.valueChanges
      .pipe(startWith(this.formGroup.value), pairwise(), takeUntilDestroyed(this.destroyRef$))
      .subscribe(([prev, curr]) => {
        if (prev.key !== curr.key) {
          if (!valueCtrl.hasValidator(Validators.required)) {
            valueCtrl.addValidators(Validators.required);
          }
          this.formGroup.patchValue({ operator: CriteriaOperator.Equals, value: null }, { emitEvent: false });
        } else if (prev.operator !== curr.operator) {
          if (isUnaryOperator(curr.operator)) {
            valueCtrl.removeValidators(Validators.required);
          } else if (isUnaryOperator(prev.operator)) {
            valueCtrl.addValidators(Validators.required);
          }

          if (!valuePreservingTransitions[prev.operator].includes(curr.operator)) {
            valueCtrl.setValue(null);
          }
        }
      });
  }
}
